import type { WitnessLeaf, WitnessVerdict } from "@ondo/shared";

import {
  WITNESS_CHECK_LABELS,
  WITNESS_COPY,
  WITNESS_STATE_LABELS,
  WITNESS_SUBJECT_LABELS,
} from "./witness.constants.ts";

/** What the controller has so far. */
export type WitnessLoad =
  | { kind: "loading" }
  | { kind: "failed" }
  | { kind: "loaded"; leaves: WitnessLeaf[]; verdicts: ReadonlyMap<string, WitnessVerdict> };

export interface WitnessLineView {
  id: string;
  subject: string;
  state: string;
  witnessedAt: string;
  anchor:
    | { kind: "open"; label: string }
    | { kind: "awaiting-block"; label: string }
    | { kind: "anchored"; label: string; blockHeight: number };
  check: { kind: WitnessVerdict["kind"]; label: string; detail: string | null };
  fieldsChecked: number;
}

export type WitnessPanel =
  | { kind: "loading"; message: string }
  | { kind: "failed"; message: string }
  | { kind: "empty"; message: string }
  | { kind: "listed"; lines: WitnessLineView[]; anchored: number; total: number };

const WHEN = new Intl.DateTimeFormat("en-GB", {
  dateStyle: "medium",
  timeStyle: "short",
  timeZone: "UTC",
});

/** A moment, in UTC and said so: an anchor's time is not a local one. */
export function formatWitnessMoment(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return `${WHEN.format(date)} ${WITNESS_COPY.utc}`;
}

export function stateLabel(state: string): string {
  return WITNESS_STATE_LABELS[state] ?? state;
}

function anchorOf(leaf: WitnessLeaf): WitnessLineView["anchor"] {
  if (leaf.position === null || leaf.root === null) {
    return { kind: "open", label: WITNESS_COPY.openHour };
  }
  const height = leaf.anchor?.blockHeight ?? null;
  if (height === null) return { kind: "awaiting-block", label: WITNESS_COPY.awaitingBlock };
  return {
    kind: "anchored",
    label: `${WITNESS_COPY.block} ${height.toLocaleString("en-GB")}`,
    blockHeight: height,
  };
}

function checkOf(verdict: WitnessVerdict | undefined): WitnessLineView["check"] {
  if (verdict === undefined) {
    return { kind: "awaiting-close", label: WITNESS_CHECK_LABELS["awaiting-close"], detail: null };
  }
  switch (verdict.kind) {
    case "verified":
      return { kind: "verified", label: WITNESS_CHECK_LABELS.verified, detail: null };
    case "awaiting-close":
      return {
        kind: "awaiting-close",
        label: WITNESS_CHECK_LABELS["awaiting-close"],
        detail: null,
      };
    case "shredded":
      return { kind: "shredded", label: WITNESS_CHECK_LABELS.shredded, detail: WITNESS_COPY.shredded };
    case "failed":
      return {
        kind: "failed",
        label: WITNESS_CHECK_LABELS.failed,
        detail: verdict.field === undefined ? verdict.reason : `${verdict.reason} (${verdict.field})`,
      };
  }
}

export function toWitnessPanel(load: WitnessLoad): WitnessPanel {
  if (load.kind === "loading") return { kind: "loading", message: WITNESS_COPY.loading };
  if (load.kind === "failed") return { kind: "failed", message: WITNESS_COPY.failed };
  if (load.leaves.length === 0) return { kind: "empty", message: WITNESS_COPY.empty };

  // Newest first: the question a reader brings to this tab is "what has
  // happened to this record lately, and is it anchored yet".
  const lines = [...load.leaves]
    .sort((a, b) => b.witnessedAt.localeCompare(a.witnessedAt))
    .map((leaf): WitnessLineView => {
      const verdict = load.verdicts.get(leaf.id);
      return {
        id: leaf.id,
        subject: WITNESS_SUBJECT_LABELS[leaf.subject] ?? leaf.subject,
        state: stateLabel(leaf.state),
        witnessedAt: formatWitnessMoment(leaf.witnessedAt),
        anchor: anchorOf(leaf),
        check: checkOf(verdict),
        fieldsChecked:
          verdict?.kind === "verified" || verdict?.kind === "awaiting-close"
            ? verdict.fields.length
            : 0,
      };
    });

  return {
    kind: "listed",
    lines,
    anchored: lines.filter((line) => line.anchor.kind === "anchored").length,
    total: lines.length,
  };
}

/**
 * The bundle a reader takes away: the leaves exactly as served, plus a note
 * saying what each part is and how to check it without ONDO. Nothing is
 * recomputed or summarised here — a bundle that editorialised would be a
 * second copy of the proof, and the second copy is the one that drifts.
 */
export function witnessBundle(workId: string, leaves: WitnessLeaf[]): string {
  return JSON.stringify(
    {
      format: "ondo-witness-bundle/1",
      workId,
      exportedAt: new Date().toISOString(),
      howToCheck: [
        "For each leaf: SHA-256(hex-decode(revealed[f].salt) || utf8(revealed[f].text)) must equal JSON.parse(preimage).fields[f] for every revealed field f.",
        "SHA-256(hex-decode(salt) || utf8(preimage)) must equal leafHash.",
        "Climb path from leafHash: for each step, hash(current || sibling) when side is 'right', hash(sibling || current) when 'left'; the result must equal root. A path is never empty.",
        "Base64-decode anchor.proof to a .ots file and run `ots verify -d <root> <file>.ots` against a Bitcoin node: the root was stamped as a bare digest, so the tool must be handed it. `ots info` names the digest the receipt is about, which must equal root, and the block attested must be at or below anchor.blockHeight.",
      ],
      leaves,
    },
    null,
    2,
  );
}
